<template>
  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        <i class="el-icon-user-solid"></i>
        <h1>复课证明</h1>
      </div>

      <el-tabs v-model="activeTab" class="login-tabs">
        <!-- 学生登录 -->
        <el-tab-pane label="学生登录" name="student">
          <el-form :model="studentForm" :rules="studentRules" ref="studentFormRef" label-width="100px"
            label-position="left" class="login-form">
            <el-form-item label="身份证号" prop="idCard">
              <el-input v-model="studentForm.idCard" placeholder="请输入身份证号" type="text" maxlength="18" show-word-limit
                @blur="checkIdCard" autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item label="学生姓名" prop="name">
              <el-input v-model="studentForm.name" placeholder="请输入学生姓名" type="text" show-word-limit
                autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item label="学校" prop="school">
              <el-select v-model="studentForm.school" placeholder="请选择学校" class="school-select" filterable clearable>
                <el-option v-for="school in schoolList" :key="school.id" :label="school.schName"
                  :value="school.schName"></el-option>
              </el-select>
              <div class="school-tip">
                <p>
                  <i class="el-icon-info"></i>
                  仅支持兴隆街道范围内学校开具复课证明，如未找到学生学校，请线下办理或咨询校医前往对应基层医疗机构开具证明。
                </p>
              </div>
            </el-form-item>

            <el-form-item class="login-btn-item">
              <el-button type="primary" @click="handleStudentLogin" class="login-btn" :loading="submissionLoading">
                <span v-if="!submissionLoading">提交</span>
                <span v-if="submissionLoading">提交中...</span>
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>

        <!-- 医生登录 -->
        <el-tab-pane label="医生登录" name="doctor">
          <el-form :model="doctorForm" :rules="doctorRules" ref="doctorFormRef" label-width="100px"
            label-position="left" class="login-form">
            <el-form-item label="手机号" prop="phone">
              <el-input v-model="doctorForm.phone" placeholder="请输入手机号" type="text" maxlength="11" show-word-limit
                autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item label="密码" prop="password">
              <el-input v-model="doctorForm.password" placeholder="请输入密码" type="password" autocomplete="off"></el-input>
            </el-form-item>

            <el-form-item class="login-btn-item">
              <el-button type="primary" @click="handleDoctorLogin" class="login-btn" :loading="submissionLoading">
                <span v-if="!submissionLoading">登录</span>
                <span v-if="submissionLoading">登录中...</span>
              </el-button>
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import request from '@/utils/request';
import { setToken } from '@/utils/auth'; // 导入auth工具类
import store from '@/store';

// 路由实例
const route = useRoute();
const router = useRouter();

// 响应式状态
const activeTab = ref('student');
const submissionLoading = ref(false); // 统一提交loading
const idCardCheckLoading = ref(false);
const schoolList = ref([]);
const existingQueId = ref(null);

// 表单Ref
const studentFormRef = ref(null);
const doctorFormRef = ref(null);

// 学生表单数据
const studentForm = reactive({
  name: '', // 学生姓名（对应 queChildrenName）
  idCard: '', // 学生身份证号（对应 queChildrenSfz）
  school: '', // 学校（对应 queChildrenSchool）
  grade: '', // 年级（对应 queChildrenGrade）
  class: '', // 班级（对应 queChildrenClass）
  gender: '', // 性别（对应 queChildrenGender）
  address: '', // 地址（对应 queChildrenAddress）
  parentName: '', // 家长姓名（对应 queParentName）
  parentPhone: '', // 家长电话（对应 quePhone）
  relationship: '', // 家长与学生关系（对应 queChildrenRelationship）
  fallTime: '', // 发病时间（对应 queFallTime）
  diagnosisTime: '', // 确诊时间（对应 queDiagnosisTime）
  disease: '', // 疾病名称（对应 queDisease）
  symptoms: '', // 症状（对应 queSymptoms）
  symptomsOther: '', // 其他症状（对应 queSymptomsOther）
  symptomsTime: '', // 症状出现时间（对应 queSymptomsTime）
  hospital: '', // 就诊医院（对应 queHospital）
  parentUrl: '', // 家长相关链接（对应 queParentUrl）
  queId: '' // 申请单ID（对应 queId）
});

// 学生表单校验规则
const studentRules = ref({
  name: [
    { required: true, message: '请输入学生姓名', trigger: 'blur' },
    { pattern: /^[\u4e00-\u9fa5a-zA-Z]+$/, message: '姓名仅支持中文和英文', trigger: 'blur' },
    { min: 2, max: 10, message: '姓名长度为2-10位', trigger: 'blur' }
  ],
  idCard: [
    { required: true, message: '请输入身份证号', trigger: 'blur' },
    { pattern: /(^\d{18}$)|(^\d{17}(\d|X|x)$)/, message: '请输入18位有效身份证号', trigger: 'blur' }
  ],
  school: [
    { required: true, message: '请选择学校', trigger: 'change' }
  ]
});

// 医生表单数据
const doctorForm = reactive({
  phone: '',
  password: ''
});

// 医生表单校验规则
const doctorRules = ref({
  phone: [
    { required: true, message: '请输入手机号', trigger: 'blur' },
    { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ]
});

/**
 * 组件挂载时执行
 */
onMounted(() => {
  // 检查是否已登录，避免重复登录
  const userInfo = localStorage.getItem('userInfo');
  if (userInfo) {
    const redirect = route.query.redirect || (JSON.parse(userInfo).role === 'doctor' ? '/yishengshenhe' : '/survey');
    router.push(redirect);
    return;
  }
  fetchSchoolList();
});

/**
 * 获取学校列表
 */
const fetchSchoolList = () => {
  request({
    url: '/api/schoolInfos/all',
    method: 'get'
  }).then(response => {
    if (response.code === 200 && Array.isArray(response.data)) {
      schoolList.value = response.data;
    } else {
      ElMessage.error('获取学校列表失败！');
    }
  }).catch(error => {
    ElMessage.error('网络异常，获取学校列表失败！');
    console.error(error);
  });
};

/**
 * 身份证号校验
 */
const checkIdCard = () => {
  const idCard = studentForm.idCard.trim();
  existingQueId.value = null;

  if (!idCard || !/(^\d{18}$)|(^\d{17}(\d|X|x)$)/.test(idCard)) {
    studentForm.name = '';
    studentForm.school = '';
    return;
  }

  idCardCheckLoading.value = true;
  request({
    url: '/api/questionnaires/getByChildrenSfz',
    method: 'get',
    params: { childrenSfz: idCard }
  }).then(response => {
    idCardCheckLoading.value = false;
    if (response.code === 200 && response.data) {
      existingQueId.value = response.data.queId;
      studentForm.name = response.data.queChildrenName || '';
      studentForm.school = response.data.queChildrenSchool || '';
      ElMessage.success('已查询到学生信息');
    } else {
      existingQueId.value = null;
      studentForm.name = '';
      studentForm.school = '';
      if (response.code === 500) {
        ElMessage.warning('未查询到学生信息，请手动填写姓名和学校');
      } else {
        ElMessage.error('身份证号验证失败：' + (response.message || '未知错误'));
      }
    }
  }).catch(error => {
    idCardCheckLoading.value = false;
    ElMessage.error('网络异常，身份证号验证失败！');
    console.error(error);
  });
};

/**
 * 学生提交逻辑
 */
const handleStudentLogin = async () => {
  if (!studentFormRef.value) return;

  try {
    await studentFormRef.value.validate();
    submissionLoading.value = true;

    const postData = {
      queChildrenSfz: studentForm.idCard.trim(),
      queChildrenName: studentForm.name.trim(),
      queChildrenSchool: studentForm.school,
      queId: existingQueId.value
    };

    const response = await request({
      url: '/api/questionnaires/addOrUpdate',
      method: 'post',
      data: postData
    });

    if (response.code === 200 && response.data) {
      // 关键修改：学生Token从 exam202510 字段获取
      const studentToken = response.data.exam202510;
      if (!studentToken) {
        throw new Error('未获取到学生认证Token');
      }

      // 存储Token和用户信息（完整同步接口返回字段 + 表单字段）
      setToken(studentToken);

      // 构造完整用户信息对象：接口返回字段优先，表单字段兜底
      const userInfo = {
        // 接口返回的所有字段（直接解构，确保不遗漏）
        ...response.data,
        // 表单补充字段（接口未返回时使用表单输入值）
        queChildrenGrade: response.data.queChildrenGrade || studentForm.grade.trim(),
        queChildrenClass: response.data.queChildrenClass || studentForm.class.trim(),
        queChildrenGender: response.data.queChildrenGender || studentForm.gender.trim(),
        queChildrenAddress: response.data.queChildrenAddress || studentForm.address.trim(),
        queParentName: response.data.queParentName || studentForm.parentName.trim(),
        quePhone: response.data.quePhone || studentForm.parentPhone.trim(),
        queChildrenRelationship: response.data.queChildrenRelationship || studentForm.relationship.trim(),
        queFallTime: response.data.queFallTime || studentForm.fallTime.trim(),
        queDiagnosisTime: response.data.queDiagnosisTime || studentForm.diagnosisTime.trim(),
        queDisease: response.data.queDisease || studentForm.disease.trim(),
        queSymptoms: response.data.queSymptoms || studentForm.symptoms.trim(),
        queSymptomsOther: response.data.queSymptomsOther || studentForm.symptomsOther.trim(),
        queSymptomsTime: response.data.queSymptomsTime || studentForm.symptomsTime.trim(),
        queHospital: response.data.queHospital || studentForm.hospital.trim(),
        queParentUrl: response.data.queParentUrl || studentForm.parentUrl.trim(),
        // 角色标识（固定为student）
        role: 'student'
      };

      // 1. 存储完整用户信息到localStorage（供其他页面统一读取）
      localStorage.setItem('userInfo', JSON.stringify(userInfo));

      // 2. 存储关键字段（方便快速取用，可选但推荐）
      localStorage.setItem('queId', userInfo.queId);
      localStorage.setItem('queChildrenSfz', userInfo.queChildrenSfz);
      localStorage.setItem('queChildrenName', userInfo.queChildrenName);
      localStorage.setItem('role', 'student');

      // 3. 初始化表单完成状态（供Survey页面使用）
      localStorage.setItem('bingliCompleted', 'false');
      localStorage.setItem('xinxiCompleted', 'false');
      localStorage.setItem('zhengmingCompleted', 'false');
      localStorage.setItem('uploadedFilePath', userInfo.queChildrenFile || ''); // 同步已上传的文件路径

      // 根据 existingQueId 的值来判断跳转方向
      // existingQueId 不为 null，说明是更新操作
      if (existingQueId.value) {
        ElMessage.success('学生信息已更新，请继续填写问卷。');
        router.push('/survey');
      }
      // existingQueId 为 null，说明是新增操作
      else {
        ElMessage.success('学生信息已创建，请阅读并签署复课证明同意书。');
        router.push('/consent');
      }

    } else {
      ElMessage.error('操作失败：' + (response.message || '服务器内部错误'));
    }
  } catch (error) {
    console.error('学生登录失败:', error);
    ElMessage.error(error.message || '表单验证失败，请检查输入内容');
  } finally {
    submissionLoading.value = false;
  }
};

/**
 * 医生登录核心逻辑（Query 参数版）
 */
/**
 * 医生登录核心逻辑（确保 userInfo 格式正确）
 */
const handleDoctorLogin = async () => {
  if (!doctorFormRef.value) return;

  try {
    await doctorFormRef.value.validate();
    submissionLoading.value = true;

    const loginParams = {
      phone: doctorForm.phone.trim(),
      password: doctorForm.password.trim()
    };

    const response = await request({
      url: '/api/physicians/checkLogin',
      method: 'post',
      params: loginParams
    });

    if (response.code === 200 && response.data) {
      const doctorToken = response.data;
      setToken(doctorToken);

      // 关键：确保 userInfo 是标准 JSON 格式，role 字段明确
      const userInfo = JSON.stringify({
        phone: loginParams.phone,
        role: 'doctor' // 强制指定角色为 doctor
      });
      localStorage.setItem('userInfo', userInfo); // 直接存储 JSON 字符串，避免格式异常
      localStorage.setItem('physicianPhone', loginParams.phone);

      // 同步 Vuex 角色（双重保障）
      store.commit('setUserRoles', ['doctor']);
      console.log('Vuex 角色设置:', store.getters.userRoles);

      ElMessage.success(response.message || '医生登录成功！');

      // 强制跳转，避免循环（使用 replace 替换历史记录）
      setTimeout(() => {
        router.replace('/yishengshenhe'); // 用 replace 避免回退时再次触发循环
      }, 300);
    } else {
      ElMessage.error(`登录失败：${response.message || '服务器校验失败'}`);
    }
  } catch (error) {
    console.error('医生登录失败:', error);
    ElMessage.error(error.message || '网络异常，登录失败！');
  } finally {
    submissionLoading.value = false;
  }
};
</script>

<style scoped>
/* 页面整体样式 */
.login-container {
  width: 100%;
  height: 100vh;
  background-image: url('../assets/beijing2.png');
  background-repeat: repeat;
  background-position: center;
  background-attachment: fixed;
  background-size: cover;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

/* 登录卡片样式 */
.login-card {
  width: 100%;
  max-width: 500px;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 30px 40px;
  box-sizing: border-box;
}

/* 标题区域样式 */
.login-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.login-header i {
  font-size: 24px;
  color: #409eff;
  margin-right: 10px;
}

.login-header h1 {
  font-size: 22px;
  color: #333;
  font-weight: 500;
}

/* 标签页样式 */
.login-tabs {
  margin-bottom: 10px;
}

/* 表单样式 */
.login-form {
  width: 100%;
  margin-top: 20px;
}

/* 输入框/选择框通用样式 */
.el-input,
.school-select {
  width: 100%;
}

/* 学校选择提示样式 */
.school-tip {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fef0f0;
  border-radius: 4px;
}

.school-tip p {
  font-size: 12px;
  color: #e53e3e;
  line-height: 1.5;
  margin: 0;
}

.school-tip i {
  margin-right: 4px;
}

/* 登录按钮区域样式 */
.login-btn-item {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.login-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}

/* 响应式样式 */
@media (max-width: 480px) {
  .login-card {
    padding: 20px 15px;
  }

  .login-header h1 {
    font-size: 20px;
  }

  .el-form-item {
    margin-bottom: 15px;
  }

  .checking-tip {
    display: flex;
    align-items: center;
    margin-top: 8px;
    color: #409eff;
    font-size: 12px;
  }

  .checking-tip i {
    margin-right: 4px;
    animation: loading 1s linear infinite;
  }

  @keyframes loading {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }
}
</style>